import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';
import { usePremiumFlow } from '@/hooks/queries/useAnalytics';
import { PremiumFlowChart } from './PremiumFlowChart';
import { PERIODS, type Period } from './periods';

export interface PremiumFlowSankeyProps {
  period: Period;
  onPeriodChange: (period: Period) => void;
}

/** Props-driven: the page owns the period, so it is one shared `?period=` for the whole page. */
export function PremiumFlowSankey({ period, onPeriodChange }: PremiumFlowSankeyProps) {
  const { data: flow, isLoading } = usePremiumFlow({ period });

  return (
    <Card data-testid="premium-flow">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
        <CardTitle>Premium Flow</CardTitle>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => onPeriodChange(p.value)}
              aria-pressed={period === p.value}
              className={cn(
                'px-2 py-1 text-xs font-medium rounded transition-colors',
                period === p.value ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:bg-gray-100',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center h-80"><Spinner /></div>
        ) : !flow || flow.collected === 0 ? (
          <div className="h-80 flex items-center justify-center">
            <EmptyState icon={<NoDataIcon />} title="No premium collected" description="Open some credit trades to see where your premium goes" />
          </div>
        ) : (
          <PremiumFlowChart key={period} flow={flow} />
        )}
      </CardContent>
    </Card>
  );
}
